import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Building2, Users, ChevronLeft } from 'lucide-react';

// Calculate monthly price per team based on quantity (annual prices divided by 12)
const calculateMonthlyPricePerTeam = (quantity: number): number => {
  if (quantity >= 50) return Number((51 / 12).toFixed(2)); // £4.25/team/month
  if (quantity >= 25) return Number((59 / 12).toFixed(2)); // £4.92/team/month
  if (quantity >= 11) return Number((67 / 12).toFixed(2)); // £5.58/team/month
  return Number((79 / 12).toFixed(2)); // £6.58/team/month (1-10 teams)
};

// Get tier name based on quantity
const getTierName = (quantity: number): string => {
  if (quantity >= 50) return '50+ Teams';
  if (quantity >= 25) return '25-49 Teams';
  if (quantity >= 11) return '11-24 Teams';
  return '1-10 Teams';
};

const tiers = [
  { label: '1-10 teams', price: '£6.58/team/month' },
  { label: '11-24 teams', price: '£5.58/team/month' },
  { label: '25-49 teams', price: '£4.92/team/month' },
  { label: '50+ teams', price: '£4.25/team/month' },
];

export const SelectSubscriptionTierPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state?.registrationData;

  const [teamCount, setTeamCount] = useState<string>('');
  const [error, setError] = useState<string>('');

  if (!registrationData) {
    navigate('/administration-club/register');
    return null;
  }

  const handleTeamCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setTeamCount(value);
      setError('');
    }
  };

  const teamCountNumber = teamCount ? parseInt(teamCount, 10) : 0;
  const monthlyPricePerTeam = teamCountNumber > 0 ? calculateMonthlyPricePerTeam(teamCountNumber) : 0;
  const totalMonthlyPrice = Number((teamCountNumber * monthlyPricePerTeam).toFixed(2));
  const tierName = teamCountNumber > 0 ? getTierName(teamCountNumber) : '';

  const handleContinue = () => {
    if (!teamCount || teamCountNumber < 1) {
      setError('Please enter the number of teams (minimum 1)');
      return;
    }

    navigate('/administration-club/confirm-subscription', {
      state: {
        registrationData,
        selectedTier: {
          quantity: teamCountNumber,
          pricePerTeam: monthlyPricePerTeam,
          totalPrice: totalMonthlyPrice,
          label: `${teamCountNumber} ${teamCountNumber === 1 ? 'Team' : 'Teams'}`,
          tierName,
        }
      }
    });
  };

  const handleBack = () => {
    navigate('/administration-club/register', {
      state: { formData: registrationData }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Side - Branding & Pricing Info */}
        <div className="hidden lg:block">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
              <span className="text-lg font-normal text-gray-500 ml-2">for Clubs</span>
            </h1>
            <p className="text-xl text-gray-600">
              Volume pricing that scales with your club
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Simple Per-Team Pricing</h3>
                <p className="text-gray-600 text-sm">One price per team, per month. No hidden fees or per-user charges.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Volume Discounts</h3>
                <p className="text-gray-600 text-sm">The more teams you manage, the less you pay per team. Discounts apply automatically.</p>
              </div>
            </div>
          </div>

          {/* Pricing Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Pricing Per Team Per Month</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {tiers.map((tier) => (
                <div key={tier.label} className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-gray-700">{tier.label}</span>
                  <span className="text-sm font-semibold text-green-600">{tier.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Team Count & Calculation */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 lg:p-10">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-3xl font-bold mb-1">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
            </h1>
            <p className="text-gray-600">Select your subscription</p>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Subscription</h2>
            <p className="text-gray-600">
              Enter the number of teams you manage. Volume discounts apply automatically.
            </p>
          </div>

          {/* Mobile Pricing Table */}
          <div className="lg:hidden bg-gray-50 rounded-xl p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Pricing Per Team Per Month</h3>
            <div className="space-y-2">
              {tiers.map((tier) => (
                <div key={tier.label} className="flex justify-between items-center">
                  <span className="text-sm text-gray-700">{tier.label}</span>
                  <span className="text-sm font-semibold text-green-600">{tier.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Team Count Input */}
          <div className="mb-6">
            <label htmlFor="teamCount" className="block text-sm font-medium text-gray-700 mb-2">
              How many teams do you manage?
            </label>
            <input
              id="teamCount"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter number of teams"
              value={teamCount}
              onChange={handleTeamCountChange}
              autoFocus
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-lg font-semibold"
            />
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>

          {/* Calculation Box */}
          {teamCountNumber > 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-6 border border-green-200 mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Your Monthly Subscription</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Number of teams:</span>
                  <span className="font-medium text-gray-900">{teamCountNumber}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Pricing tier:</span>
                  <span className="font-medium text-gray-900">{tierName}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Price per team:</span>
                  <span className="font-medium text-gray-900">£{monthlyPricePerTeam}/month</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-green-300">
                  <span className="font-semibold text-gray-900">Total monthly charge:</span>
                  <span className="text-2xl font-bold text-green-600">£{totalMonthlyPrice.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex items-center justify-center gap-1 px-5 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={!teamCount || teamCountNumber < 1}
              className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to Payment
            </button>
          </div>

          <div className="text-center mt-5">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/administration-club" className="text-green-600 hover:text-green-700 font-medium">Sign in</Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};
